import {convertIntToLetters, convertLettersToInt, doWithDelay} from "../util/CommonUtils.js";
import OnPlatePosition from "../components/plate/OnPlatePosition.js";

let timer = null;
export default class PlateViewModel {
  #callbacks = {
    onNavigateOutside: [],
    onCellClick: [],
    onChangeSelection: [],
    onInjectionHover: [],
  }

  /**
   * @param {PlateList} plateList
   * @param {PlateView} view
   * @param {{showActiveWell:boolean}} opts
   */
  constructor(plateList, view, opts) {
    this._plateList = plateList;
    this._view = view;
    this.opts = Object.assign({}, ...[
      {
        showActiveWell: true,
      },
      opts
    ])
    this._activeCell = {};
    this._currentLevel = 0;

    const {cols, rows, maxDepth} = plateList.meta;

    if (maxDepth > 1) {
      let secondLvlPlate = plateList.getPlate(1);
      for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cols; col++) {
          const pos = new OnPlatePosition(row, col);
          if (secondLvlPlate.getInjection(pos))
            this._view.addMultilevelMark(pos)
        }
      }
      let paginator = this._paginator = this._view.addPaginator(plateList.meta.maxDepth);
      paginator.addEventListener('onPageClick', () => {
        let currentLevel = paginator.getCurrentPage() - 1;
        this._setCurrentLevel(currentLevel);
        const hasActiveCellAtCurrentLevel = this._activeCell && this._activeCell.level === this._currentLevel;
        this._view.setActiveCell(hasActiveCellAtCurrentLevel ? this._activeCell.pos : null);
      })
    }

    this._setCurrentLevel(this._currentLevel);
    view.onCellClick((evt) => {
      this.#callbacks.onCellClick.forEach(cb => cb(evt));
    });

    view.onChangeSelection((evt) => {
      this.#callbacks.onChangeSelection.forEach(cb => cb(evt));
    });

    view.onInjectionHover((injectionId, isHovered) => {
      this.#callbacks.onInjectionHover.forEach(cb => cb(injectionId, isHovered));
    });
  }

  getPlateId() {
    return this._plateList.id
  }

  /** @returns {string} */
  getHighlightedWellsAsString() {
    const map = new Map();
    for (const plate of this._plateList) {
      const level = plate.meta.level;
      for (const injId of plate.getCurrentSelection()) {
        const {rowIndex, colNumber} = plate.getCoordinatesByInjectionId(injId);
        const key = convertIntToLetters(rowIndex) + colNumber;
        if (map.has(key))
          map.set(key, map.get(key) + ',' + level);
        else
          map.set(key, level);
      }
    }
    let result = '';
    for (const [well, levels] of map.entries())
      result += well + ':' + levels + ';';
    return result;
  }
  getHighlightedInjectionIds(){
    const arr = [];
    for (/**@type Plate*/const plate of this._plateList) {
      arr.push(...plate.getCurrentSelection())
    }
    return arr;
  }

  getInjections(){
    return this._plateList.injections;
  }

  getAllInjectionIds(){
    return this._plateList.injections.map(injection => injection.id);
  }

  transformLiteralCoordinatesToInjectionIds(literalCoordinatesList) {
    if (!literalCoordinatesList) return [];
    let idList = [];
    for (let literalCoordinates of literalCoordinatesList) {
      const coordinates = literalCoordinates.split(':');
      const literal = coordinates[0];
      if (literal.length === 0) continue;
      const match = literal.match('([A-Z]+)([0-9]+)');
      if (!Array.isArray(match) || match.length !== 3)
        continue;
      const pos = new OnPlatePosition(convertLettersToInt(match[1]), parseInt(match[2]) - 1);
      for (let index of coordinates[1].split(',')) {
        let injection = this._plateList.getPlate(+index).getInjection(pos);
        if (injection)
          idList.push(injection.id)
      }
    }
    return idList
  }

  injectionLoaded(injectionId) {
    const result = this._plateList.find(injectionId);
    if (result) {
      const {plate, pos} = result;
      this._setCurrentLevel(plate.meta.level)
      this.setActiveCell(pos);
    } else
      this.setActiveCell(null)
  }

  changePlateLevel(direction) {
      let levelToGo = this._currentLevel;
      switch (direction) {
        case 'up':
          levelToGo++;
          break;
        case 'down':
          levelToGo--;
          break;
      }
      if (levelToGo !== this._currentLevel && levelToGo >= 0 && levelToGo < this._plateList.meta.maxDepth){
        let injection = this._plateList.getPlate(levelToGo).getInjection(this._activeCell.pos);
        if (injection){
          this._setCurrentLevel(levelToGo);
          this._view.setActiveCell(this._activeCell.pos);
          this.#callbacks.onCellClick.forEach(cb => cb({
            plateId: this._plateList.id,
            injectionId: injection.id
          }))
        }
      }
  }

  /** @return {string | null} */
  getTopmostHighlightedInjectionId() {
    /** @type {OnPlatePosition | null} */
    let topmost = null;
    let id = null;
    outer: for (const plate of this._plateList) {
      for (const injectionId of plate.getCurrentSelection()) {
        let pos = plate.getCoordinatesByInjectionId(injectionId);
        if (!topmost || pos.rowIndex < topmost.rowIndex || (pos.rowIndex === topmost.rowIndex && pos.colIndex < topmost.colIndex)) {
          topmost = pos;
          id = injectionId;
          if (topmost.rowIndex === 0 && topmost.colIndex === 0)
            break outer;
        }
      }
    }
    return id;
  }

  /** @param {OnPlatePosition} pos */
  setActiveCell(pos){
    this._activeCell = {pos, level: this._currentLevel}
    this._view.setActiveCell(pos);
  }

  /** @param {Map<string, {injection: InjectionJson, color: string}>} selectedInjections */
  toggleComparingHighlight(selectedInjections){
      this._view.toggleComparingHighlight(selectedInjections);
  }

  hasActiveCellInCurrentLevel() {
    return this._activeCell.pos != null && this._activeCell.level === this._currentLevel;
  }

  navigationKeyPressed(data) {
    const from = this._activeCell.pos;
    let pos;
    if(data.highlighted)
      pos = this.getNextHighlightedCellPosition(from, data.direction)
    else
      pos = this._getNextCell(from, data.direction);
    if (pos) {
      // well is found, triggering 'click'
      this.setActiveCell(pos);
      // load injection with 100ms delay, it is needed for fast navigation on plate
      timer = doWithDelay(timer, ()=>{
        this.#callbacks.onCellClick.forEach(cb => cb({
          plateId: this._plateList.id,
          injectionId: this.getCurrentPlate().getInjection(pos).id
        }));
      }, 100);
    } else {
      // We have reached the end of the current 'plate', now the parent component (plate list) must decide
      // what to do next: either navigate to another 'plate' or stop navigation.
      const outsideNavigationRequest = {
        sourcePlateId: this._plateList.id,
        direction: data.direction,
        navigateToHighlighted: data.highlighted
      };
      this.#callbacks.onNavigateOutside.forEach(cb => cb(outsideNavigationRequest));
    }
  }

  /**
   * @param {number} level
   * @return {Plate}
   */
  getPlate(level){
    return this._plateList.getPlate(level);
  }

  /** @return {Plate} */
  getCurrentPlate(){
    return this.getPlate(this._currentLevel)
  }

  /** @param {VisualizationDataSelection} selection */
  selectionChanged(selection){
    for (const plate of this._plateList)
      plate.setSelection(selection.getInjectionIds())
    this._view._selectExclusively(this.getCurrentPlate().getCurrentSelection())
  }

  onNavigateOutside(cb) {
    this.#callbacks.onNavigateOutside.push(cb);
  }
  onCellClick(cb) {
    this.#callbacks.onCellClick.push(cb);
  }
  onChangeSelection(cb) {
    this.#callbacks.onChangeSelection.push(cb);
  }
  onInjectionHover(cb) {
    this.#callbacks.onInjectionHover.push(cb);
  }

  /** @param {number} level */
  _setCurrentLevel(level) {
    this._currentLevel = level;
    let currentPlate = this.getCurrentPlate();
    this._view.drawPlate(currentPlate);
    this._view._selectExclusively(currentPlate.getCurrentSelection());
    if (this._paginator != undefined)
      this._paginator.setCurrentPage(this._currentLevel + 1)
  }

  /**
   * @param {OnPlatePosition} from
   * @param {string} direction
   * @return {OnPlatePosition|null}
   * @private
   */
  _getNextCell(from, direction){
    let cell = null;
    switch (direction) {
      case 'ArrowLeft':
        cell = this.getCurrentPlate().getLeft(from);
        break;
      case 'ArrowUp':
        cell = this.getCurrentPlate().getAbove(from);
        break;
      case 'ArrowRight':
        cell = this.getCurrentPlate().getRight(from);
        break;
      case 'ArrowDown':
        cell = this.getCurrentPlate().getBelow(from);
    }
    return cell
  }

  /**
   * @param {OnPlatePosition} from
   * @param {string} direction
   * @return {OnPlatePosition|null}
   * @private
   */
  getNextHighlightedCellPosition(from, direction) {
    let found, canSearch = true, pos = from;
    let currentPlate = this.getCurrentPlate();
    let selection = currentPlate.getCurrentSelection();
    while (!found && canSearch){
      pos = this._getNextCell(pos, direction);
      if (pos){
        found = selection.includes(currentPlate.getInjection(pos).id)
      }
      canSearch = !!pos;
    }
    return pos;
  }
}
